import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff, Save, RotateCcw } from "lucide-react";

/**
 * Reminder settings card — toggle on/off, tweak lead time (hours) for email
 * and SMS reminders, override the email/SMS templates. Persists to the
 * backend `/api/reminders/settings` endpoint.
 */
export default function ReminderSettings() {
  const [state, setState] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/reminders/settings").then(({ data }) => {
      setState(data?.settings || null);
      setDefaults(data?.defaults || null);
    }).catch(() => {});
  };

  useEffect(load, []);

  if (!state) {
    return (
      <div className="text-sm text-[var(--peak-muted)]" data-testid="reminder-settings-loading">Loading…</div>
    );
  }

  const upd = (patch) => setState({ ...state, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/reminders/settings", state);
      setState(data?.settings || state);
      toast.success("Reminder settings saved");
    } catch (e) {
      toast.error("Failed to save reminders");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => {
    if (!defaults) return;
    setState({ ...defaults });
    toast("Reverted to defaults — click Save to persist");
  };

  return (
    <div className="space-y-6" data-testid="reminder-settings">
      {/* Toggle */}
      <div className="flex items-start justify-between gap-4 pb-6 border-b border-[var(--peak-border)]">
        <div className="min-w-0">
          <div className="section-label">Automatic reminders</div>
          <div className="text-sm text-[var(--peak-muted)] mt-1 max-w-xl">
            When enabled, Peak automatically sends an email + SMS reminder before every booking so no-shows drop.
          </div>
        </div>
        <button
          type="button"
          onClick={() => upd({ enabled: !state.enabled })}
          data-testid="reminder-toggle"
          data-active={state.enabled ? "1" : "0"}
          className={`flex-none inline-flex items-center gap-2 px-4 py-2 border-2 font-heading font-bold uppercase text-xs tracking-wider transition-colors ${
            state.enabled
              ? "border-[var(--peak-accent)] bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)]"
              : "border-[var(--peak-ink)] text-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-[var(--peak-bg)]"
          }`}
        >
          {state.enabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          {state.enabled ? "On" : "Off"}
        </button>
      </div>

      {/* Lead times */}
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <div className="section-label mb-2">Email · hours before</div>
          <input
            type="number"
            min={0}
            max={720}
            value={state.email_hours_before}
            onChange={(e) => upd({ email_hours_before: parseInt(e.target.value || "0", 10) })}
            data-testid="reminder-email-hours"
            className="brutalist-input"
          />
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mt-1">
            Set to 0 to skip email reminders
          </div>
        </label>
        <label className="block">
          <div className="section-label mb-2">SMS · hours before</div>
          <input
            type="number"
            min={0}
            max={720}
            value={state.sms_hours_before}
            onChange={(e) => upd({ sms_hours_before: parseInt(e.target.value || "0", 10) })}
            data-testid="reminder-sms-hours"
            className="brutalist-input"
          />
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mt-1">
            Requires contact to have a phone
          </div>
        </label>
      </div>

      {/* Templates */}
      <div className="space-y-4 pt-6 border-t border-[var(--peak-border)]">
        <div>
          <div className="section-label mb-1">Templates</div>
          <div className="text-xs text-[var(--peak-muted)]">
            Available variables: <code className="font-mono text-[var(--peak-ink)]">{"{name}"}</code>, <code className="font-mono text-[var(--peak-ink)]">{"{calendar_name}"}</code>, <code className="font-mono text-[var(--peak-ink)]">{"{when}"}</code>, <code className="font-mono text-[var(--peak-ink)]">{"{time}"}</code>, <code className="font-mono text-[var(--peak-ink)]">{"{hours}"}</code>, <code className="font-mono text-[var(--peak-ink)]">{"{cancel_url}"}</code>
          </div>
        </div>

        <label className="block">
          <div className="section-label mb-2">Email subject</div>
          <input
            type="text"
            value={state.email_subject || ""}
            onChange={(e) => upd({ email_subject: e.target.value })}
            data-testid="reminder-email-subject"
            className="brutalist-input"
            maxLength={200}
          />
        </label>

        <label className="block">
          <div className="section-label mb-2">Email body</div>
          <textarea
            value={state.email_body || ""}
            onChange={(e) => upd({ email_body: e.target.value })}
            data-testid="reminder-email-body"
            className="w-full border-2 border-[var(--peak-border)] bg-transparent p-3 text-sm font-mono text-[var(--peak-ink)] focus:border-[var(--peak-accent)] outline-none"
            rows={6}
            maxLength={4000}
          />
        </label>

        <label className="block">
          <div className="section-label mb-2">SMS body</div>
          <textarea
            value={state.sms_body || ""}
            onChange={(e) => upd({ sms_body: e.target.value })}
            data-testid="reminder-sms-body"
            className="w-full border-2 border-[var(--peak-border)] bg-transparent p-3 text-sm font-mono text-[var(--peak-ink)] focus:border-[var(--peak-accent)] outline-none"
            rows={3}
            maxLength={320}
          />
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mt-1">
            {(state.sms_body || "").length} / 320 chars
          </div>
        </label>
      </div>

      <div className="flex items-center gap-3 pt-4">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="reminder-save"
          className="inline-flex items-center gap-2 px-5 py-2 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] font-heading font-bold uppercase text-xs tracking-wider hover:bg-[var(--peak-ink)] hover:text-[var(--peak-bg)] transition-colors disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving…" : "Save reminder settings"}
        </button>
        <button
          type="button"
          onClick={resetAll}
          data-testid="reminder-reset"
          className="inline-flex items-center gap-2 px-3 py-2 text-[var(--peak-muted)] hover:text-[var(--peak-ink)] font-mono text-xs uppercase tracking-wider"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Reset to defaults
        </button>
      </div>
    </div>
  );
}
